<template>
  <base-editor-page>
    <template v-slot:description>
      <p>
        View important information or adjust the title of your Inbox. Your project name will appear
        to your participants to help give them context on the exercise.
      </p>
    </template>
    <template v-slot:body>
      <div class="row">
        <div class="col-md-6 col-sm-12 col-lg-12">
          <form class="form-horizontal">
            <div class="form-group">
              <label class="col-sm-5 col-lg-2" for="version-name">Project Name: </label>
              <div class="col-sm-7 col-lg-4">
                <input
                  class="form-control"
                  v-model="localVersionName"
                  @input="debouncedHandleSave"
                  id="version-name"
                />
              </div>
            </div>
            <div class="form-group" v-if="dateCreated">
              <label class="col-sm-5 col-lg-2" for="version-name">Date Created: </label>
              <div class="col-sm-7 col-lg-4">
                {{ dateCreated }}
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-5 col-lg-2" for="version-name">Authored By: </label>
              <div class="col-sm-7 col-lg-4">
                {{ authorName || 'Internal Build' }}
              </div>
            </div>
            <div class="form-group" v-if="latestLog.time">
              <label class="col-sm-5 col-lg-2" for="version-name">Last Updated: </label>
              <div class="col-sm-7 col-lg-4">{{ latestLog.authorName }} - {{ latestTime }}</div>
            </div>
            <template v-if="isAdmin">
              <div class="form-group">
                <label class="col-sm-5 col-lg-2" for="demo-link">Active Demo Link: </label>
                <div class="col-sm-7 col-lg-10">
                  <input
                    id="demo-link"
                    ref="demoLink"
                    v-model="demoLink"
                    type="text"
                    class="form-control demo-input"
                    readonly="readonly"
                  />
                  <span>
                    <button class="btn button-copy" type="button" @click="onCopy">
                      <span class="fas fa-copy" aria-hidden="true"> </span>
                      {{ linkCopied ? 'Link Copied' : 'Copy Link' }}
                    </button>
                  </span>
                  <button class="btn btn-default ml-10" @click.prevent="demoSetting">
                    <span class="fas fa-cog"></span> Settings
                  </button>

                  <demo-modal
                    :versionKey="versionKey"
                    :demoEmail="demoEmail"
                    :versionConfig="versionConfig"
                  ></demo-modal>
                </div>
              </div>

              <div class="form-group">
                <label for="proj-description" class="col-sm-5 col-lg-2"
                  >Project Description:
                </label>
                <div class="col-sm-7 col-lg-4">
                  <textarea
                    id="proj-description"
                    class="form-control proj-desc-textarea"
                    name="proj-desc"
                    rows="4"
                    v-model="projectDescription"
                    @input="debouncedHandleSave"
                  ></textarea>
                </div>
              </div>
              <div class="form-group">
                <label for="featured-proj" class="col-sm-5 col-lg-2">Featured Projects: </label>

                <span class="ml-15">Off/On </span>
                <div class="col-sm-7 col-lg-10" style="margin-top: -20px">
                  <v-switch
                    style="zoom: 0.9; padding-left: 65px"
                    :selected="isFeaturedProject"
                    :emit-on-mount="false"
                    data-testid="feature-proj-toggle"
                    @input="
                      (val) => {
                        handleIsFeaturedToggle(val);
                      }
                    "
                  >
                  </v-switch>
                </div>
              </div>
              <div class="form-group">
                <label for="play-link" class="col-sm-5 col-lg-2">Play Link: </label>

                <span class="ml-15">Off/On </span>
                <div class="col-sm-7 col-lg-10" style="margin-top: -20px">
                  <v-switch
                    style="zoom: 0.9; padding-left: 65px"
                    :selected="featuredProjectObj.hasPlayLink"
                    :disabled="!isFeaturedProjectCheck"
                    data-testid="playlink-toggle"
                    @input="
                      (val) => {
                        handleIsPlayLink(val);
                      }
                    "
                  >
                  </v-switch>
                </div>
              </div>
              <div class="form-group">
                <label for="download-proj" class="col-sm-5 col-lg-2">Download Project: </label>

                <span class="ml-15">Off/On</span>
                <div class="col-sm-7 col-lg-10" style="margin-top: -20px">
                  <v-switch
                    style="zoom: 0.9; padding-left: 65px"
                    :selected="featuredProjectObj.hasDownloadProject"
                    :disabled="!isFeaturedProjectCheck"
                    data-testid="download-proj-toggle"
                    @input="
                      (val) => {
                        handleIsDownloadProj(val);
                      }
                    "
                  >
                  </v-switch>
                </div>
              </div>
              <div class="form-group">
                <label class="col-sm-5 col-lg-2" for="video-url">Video URL: </label>
                <div class="col-sm-7 col-lg-4" style="margin-right: -20px">
                  <input
                    class="form-control"
                    v-model="featuredProjectObj.youtubeLink"
                    id="video-url"
                    placeholder="Enter YouTube URL"
                  />
                </div>
                <div class="row mt-5">
                  <span class="ml-15">Off/On</span>
                </div>
                <div style="margin-top: -25px">
                  <v-switch
                    style="zoom: 0.9; padding-left: 85px"
                    :selected="featuredProjectObj.hasYoutubeUrl"
                    :disabled="isValidYoutubeLink || !isFeaturedProjectCheck"
                    data-testid="watch-video-toggle"
                    @input="
                      (val) => {
                        handleYoutubeVideoToggle(val);
                      }
                    "
                  >
                  </v-switch>
                </div>
              </div>
              <div class="form-group">
                <label for="gateway-series" class="col-sm-5 col-lg-2">Gateway Series: </label>

                <span class="ml-15">Off/On </span>
                <div class="col-sm-7 col-lg-10" style="margin-top: -20px">
                  <v-switch
                    style="zoom: 0.9; padding-left: 65px"
                    :selected="isProjectGatewaySeries"
                    :emit-on-mount="false"
                    data-testid="gateway-series-toggle"
                    @input="
                      (val) => {
                        handleIsGatewaySeries(val);
                      }
                    "
                  >
                  </v-switch>
                </div>
              </div>
            </template>
          </form>
        </div>
      </div>
    </template>
  </base-editor-page>
</template>

<script>
  import BaseEditorPage from '@authoringPlatform/shared/BaseEditorPage.vue';
  import DemoModal from './DemoModal';
  import { getDemoLink } from '@authoringPlatform/../api/versions';
  import Switch from '@shared/switch.vue';
  import { mapState, mapActions } from 'vuex';
  import debounce from 'lodash/debounce';

  export default {
    name: 'Administration',

    data() {
      return {
        demoLink: '',
        linkCopied: false,
        isLoading: true,
        isFeaturedProject: 0,
        featuredProjectObj: {
          hasFeaturedProj: false,
          hasPlayLink: 0,
          hasDownloadProject: 0,
          hasYoutubeUrl: 0,
          youtubeLink: null,
        },
        localVersionName: '',
        projectDescription: '',
        isProjectGatewaySeries: 0,
      };
    },

    props: {
      versionKey: {
        type: Number,
        required: true,
      },
    },

    components: {
      BaseEditorPage,
      DemoModal,
      'v-switch': Switch,
    },

    async mounted() {
      if (this.isFeaturedProjectCheck) {
        this.featuredProjectObj = { ...this.featuredProject };
      }
      this.featuredProjectObj.hasFeaturedProj = Object.keys(this.featuredProject).length > 0;

      this.isFeaturedProjectCheck ? (this.isFeaturedProject = 1) : (this.isFeaturedProject = 0);
      this.isLoading = true;
      const { demoLink } = await getDemoLink(this.versionKey);
      this.demoLink = demoLink;
      this.isLoading = false;
      this.localVersionName = this.versionName;
      this.projectDescription = this.description;
      this.isProjectGatewaySeries = this.isGatewaySeries;
      console.log(
        `mounted isGatewaySeries: ${this.isGatewaySeries} / isProjectGatewaySeries: ${this.isProjectGatewaySeries}`
      );
    },

    methods: {
      ...mapActions('editMode/version', ['updateVersion']),

      debouncedHandleSave: debounce(function () {
        this.handleSave();
      }, 1000),

      async handleIsFeaturedToggle(val) {
        if (this.isFeaturedProject != val) {
          this.isFeaturedProject = val;
          const payload = {
            isFeaturedProject: this.isFeaturedProject,
          };
          await this.updateVersion({
            versionKey: this.versionKey,
            payload,
          });
          if (val === false) {
            this.resetFeatureProjToggle();
          }
        }
      },
      async handleIsGatewaySeries(val) {
        if (this.isProjectGatewaySeries != val) {
          this.isProjectGatewaySeries = val === true ? 1 : 0;
          const payload = {
            isGatewaySeries: this.isProjectGatewaySeries,
          };
          await this.updateVersion({
            versionKey: this.versionKey,
            payload,
          });
        }
      },
      resetFeatureProjToggle() {
        this.featuredProjectObj = {
          hasFeaturedProj: false,
          hasPlayLink: 0,
          hasDownloadProject: 0,
          hasYoutubeUrl: 0,
          youtubeLink: null,
        };
      },

      handleIsPlayLink(val) {
        if (this.featuredProjectObj.hasPlayLink != val) {
          this.featuredProjectObj.hasPlayLink = val;
          this.handleFeaturedToggleSave();
        }
      },

      handleIsDownloadProj(val) {
        if (this.featuredProjectObj.hasDownloadProject != val) {
          this.featuredProjectObj.hasDownloadProject = val;
          this.handleFeaturedToggleSave();
        }
      },
      handleYoutubeVideoToggle(val) {
        if (this.featuredProjectObj.hasYoutubeUrl != val) {
          this.featuredProjectObj.hasYoutubeUrl = val;
          this.handleFeaturedToggleSave();
        }
      },

      async handleFeaturedToggleSave() {
        const payload = {
          featuredProjectToggleObj: this.featuredProjectObj,
        };
        await this.updateVersion({
          versionKey: this.versionKey,
          payload,
        });
      },

      async handleSave() {
        const validation = this.validate();

        if (!validation.isValid) return alert(validation.message);

        const payload = {
          versionName: this.localVersionName,
          description: this.projectDescription,
        };

        await this.updateVersion({
          versionKey: this.versionKey,
          payload,
        });
      },

      validate() {
        let message = null;

        if (this.localVersionName.length === 0) message = 'Please enter a project name.';

        return {
          isValid: message === null,
          message,
        };
      },
      onCopy() {
        this.$refs.demoLink.select();
        this.linkCopied = document.execCommand('copy');
        setTimeout(() => (this.linkCopied = false), 5000);
      },
      demoSetting() {
        $('#demo-modal').modal('show');
      },
    },

    computed: {
      ...mapState('editMode/version', [
        'versionName',
        'description',
        'demoEmail',
        'creationLog',
        'authorName',
        'versionConfig',
        'featuredProject',
        'isGatewaySeries',
      ]),
      ...mapState('editMode', ['latestLog']),
      ...mapState('user', ['isAdmin']),

      isFeaturedProjectCheck() {
        return Object.keys(this.featuredProject).length > 0;
      },

      isValidYoutubeLink() {
        const isValidYoutubeUrl = (url) => {
          if (url === null || url === '') return true;
          const p =
            /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
          if (url.match(p)) {
            return false;
          }
          return true;
        };

        return isValidYoutubeUrl(this.featuredProjectObj.youtubeLink);
      },

      latestTime() {
        if (!this.latestLog.time) return '';
        const zoneName = this.$dayjs.tz.guess();
        return this.$dayjs(this.latestLog.time)
          .tz(zoneName)
          .format('dddd, MMMM D, YYYY [at] hh:mm A z');
      },

      dateCreated() {
        if (!this.creationLog.time) return '';
        const zoneName = this.$dayjs.tz.guess();
        return this.$dayjs(this.creationLog).tz(zoneName).format('MMMM D, YYYY');
      },

      customCompleteMessage() {
        this.versionConfig.completeModal ? this.versionConfig.completeModal.body : '';
      },
    },
  };
</script>
<style lang="scss">
  .toggle-container {
    display: inline-block;
  }
  #proj-description {
    border: 1px solid lightgray;
  }
  .switch-desc {
    margin-top: -20px;
  }
  .demo-input {
    width: 60%;
    margin-right: 0;
    display: inline-block;
  }
  .button-copy {
    transform: translate(-3px, -1px);
  }
  @media only screen and (max-width: 1200px) {
    .demo-input {
      width: 100%;
    }
  }
</style>
